import { useContext, useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { getGlobalTimePeriods } from 'gqlQueries/filterQueries';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig, updateTimePeriodLabels } from 'utils/helperMethods';
import { changePeriodAccordingToScreen } from 'parser/filterParser';

const PPA_SCREEN_NAME = 'home';
const PPA_TIME_PERIOD_FEATURE = 'default';

export function usePpaDefaultTimePeriod() {
    const { appConfig } = useContext(UserContext);
    const globalFilterConfig = getConfig('globalFilter', appConfig);
    const commonConfig = getConfig('commonConfig', appConfig);
    const replacementKeyMapping = globalFilterConfig?.replacementKeyMapping;
    const defaultFrequencyKey =
        commonConfig?.frequency ?? globalFilterConfig?.availableFrequenciesList?.[0]?.key ?? 'W';

    const [timePeriodFrequencies, setTimePeriodFrequencies] = useState<Record<string, any[]>>({});
    const [timePeriodLoading, setTimePeriodLoading] = useState(true);

    const [fetchGlobalTimePeriods] = useLazyQuery(getGlobalTimePeriods, {
        fetchPolicy: 'network-only',
        onCompleted: (payload) => {
            try {
                const unformattedData = JSON.parse(payload.getGlobalTimePeriods?.data ?? '{}');
                setTimePeriodFrequencies(updateTimePeriodLabels(unformattedData, replacementKeyMapping));
            } finally {
                setTimePeriodLoading(false);
            }
        },
        onError: () => setTimePeriodLoading(false),
    });

    useEffect(() => {
        fetchGlobalTimePeriods({ variables: { featureName: PPA_TIME_PERIOD_FEATURE } });
    }, [fetchGlobalTimePeriods]);

    const { startTimestamp, endTimestamp } = useMemo(() => {
        const frequencyPeriodList = timePeriodFrequencies?.[defaultFrequencyKey];
        if (!frequencyPeriodList?.length) {
            return { startTimestamp: undefined, endTimestamp: undefined };
        }

        const periodList = changePeriodAccordingToScreen(
            PPA_SCREEN_NAME,
            frequencyPeriodList,
            globalFilterConfig,
        );
        const defaultSelectedTimePeriod =
            periodList?.find((time: any) =>
                globalFilterConfig?.defaultPeriod?.includes(time.timePeriod?.toUpperCase()),
            ) || periodList?.[0];

        return {
            startTimestamp: defaultSelectedTimePeriod?.startTimestamp,
            endTimestamp: defaultSelectedTimePeriod?.endTimestamp,
        };
    }, [timePeriodFrequencies, defaultFrequencyKey, globalFilterConfig]);

    const hasValidTimePeriod =
        Number.isFinite(startTimestamp) &&
        Number.isFinite(endTimestamp) &&
        (startTimestamp ?? 0) > 0 &&
        (endTimestamp ?? 0) > 0;

    return {
        startTimestamp,
        endTimestamp,
        hasValidTimePeriod,
        isLoading: timePeriodLoading,
    };
}
